import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { AnimatePresence, motion, useReducedMotion } from 'framer-motion';
import { ArrowRight, Code, BarChart3, Workflow } from 'lucide-react';
import { Button } from './ui/button';
import { useLocale } from '../i18n/LocaleContext';

/* ------------------------------------------------------------------ */
/* Scenes — one per service, each with its own tint and workflow cards */
/* ------------------------------------------------------------------ */
const SCENES = [
  {
    id: 'web',
    tint: 'var(--tint-lilac)',
    eyebrow: 'Web Development',
    title: ['Web apps built to', 'move your business.'],
    copy: 'Modern, scalable applications in React and Next.js — designed, shipped, and tuned for performance.',
    icon: Code,
    chips: [
      { label: 'Design System', tone: '#a855f7', x: '3%', y: '12%', d: 0 },
      { label: 'Build & Deploy', tone: '#ec4899', x: '30%', y: '38%', d: 0.9 },
      { label: 'Core Web Vitals ✓', tone: '#22c55e', x: '8%', y: '64%', d: 1.7 },
    ],
    stats: [
      ['150+', 'Projects shipped'],
      ['98%', 'Success rate'],
      ['<1s', 'Median load time'],
    ],
  },
  {
    id: 'analytics',
    tint: 'var(--tint-sky)',
    eyebrow: 'Data Analytics',
    title: ['Turn raw data into', 'confident decisions.'],
    copy: 'Business intelligence, predictive analytics, and dashboards your whole team actually reads.',
    icon: BarChart3,
    chips: [
      { label: 'Sources Connected', tone: '#0ea5e9', x: '3%', y: '10%', d: 0 },
      { label: 'Model Confidence 97.3%', tone: '#a855f7', x: '24%', y: '38%', d: 0.9 },
      { label: 'Live Dashboard', tone: '#ec4899', x: '7%', y: '64%', d: 1.7 },
    ],
    stats: [
      ['24.8K', 'Users tracked'],
      ['+45%', 'Conversion lift'],
      ['7d', 'To first insight'],
    ],
  },
  {
    id: 'automation',
    tint: 'var(--tint-mint)',
    eyebrow: 'Automation',
    title: ['Hand the busywork', 'to the machines.'],
    copy: 'Intelligent workflows, automated reporting, and data pipelines that run while you sleep.',
    icon: Workflow,
    chips: [
      { label: 'Trigger: New Order', tone: '#22c55e', x: '3%', y: '11%', d: 0 },
      { label: 'Run Pipeline', tone: '#a855f7', x: '28%', y: '38%', d: 0.9 },
      { label: 'Report Sent ✓', tone: '#ec4899', x: '8%', y: '64%', d: 1.7 },
    ],
    stats: [
      ['60%', 'Less manual work'],
      ['24/7', 'Always running'],
      ['0', 'Missed handoffs'],
    ],
  },
];

const HOLD_MS = 5600;

/* Isometric glass slab stack — the "stepped platform" behind the chips */
function IsoStack({ tone, tilt }) {
  const slabs = [
    { w: 230, h: 165, x: 85, y: 5, z: 0, o: 0.45 },
    { w: 200, h: 145, x: 35, y: 55, z: 28, o: 0.68 },
    { w: 170, h: 125, x: 118, y: 100, z: 56, o: 0.88 },
  ];
  return (
    <div aria-hidden="true" className="absolute inset-0" style={{ perspective: '1100px' }}>
      <div
        className="absolute right-[5%] top-[8%] w-[340px] h-[280px] transition-transform duration-300 ease-out"
        style={{
          transform: `rotateX(${54 + tilt.y * 4}deg) rotateZ(${-42 + tilt.x * 4}deg)`,
          transformStyle: 'preserve-3d',
        }}
      >
        {slabs.map((s, i) => (
          <motion.div
            key={i}
            initial={{ opacity: 0, z: s.z - 40 }}
            animate={{ opacity: s.o, z: s.z }}
            transition={{ delay: 0.15 + i * 0.12, duration: 0.7, ease: 'easeOut' }}
            className="absolute rounded-xl"
            style={{
              width: s.w,
              height: s.h,
              left: s.x,
              top: s.y,
              transform: `translateZ(${s.z}px)`,
              background: `linear-gradient(135deg, ${tone}45, hsl(var(--raise) / 0.25))`,
              backdropFilter: 'blur(10px) saturate(150%)',
              WebkitBackdropFilter: 'blur(10px) saturate(150%)',
              border: '1px solid hsl(0 0% 100% / 0.25)',
              boxShadow:
                'inset 0 1px 0 hsl(0 0% 100% / 0.35), 0 30px 40px -20px hsl(var(--ink) / 0.3)',
            }}
          />
        ))}
      </div>
    </div>
  );
}

/* A floating liquid-glass workflow card — draggable and hoverable */
function Chip({ chip, index, reduce, constraintsRef }) {
  return (
    <motion.div
      initial={reduce ? { opacity: 1 } : { opacity: 0, y: 26, x: 18 }}
      animate={{ opacity: 1, y: 0, x: 0 }}
      exit={reduce ? { opacity: 0 } : { opacity: 0, y: -18 }}
      transition={{ delay: 0.25 + index * 0.14, duration: 0.55, ease: 'easeOut' }}
      className="absolute z-10"
      style={{ left: chip.x, top: chip.y }}
      drag={!reduce}
      dragConstraints={constraintsRef}
      dragElastic={0.18}
      dragMomentum={false}
      whileHover={reduce ? undefined : { scale: 1.08, y: -5 }}
      whileTap={reduce ? undefined : { scale: 1.12, cursor: 'grabbing' }}
    >
      <div className={reduce ? '' : 'chip-float'} style={{ animationDelay: `${chip.d}s` }}>
        <div className="liquid-glass flex items-center gap-3 rounded-2xl px-5 py-4 cursor-grab select-none">
          <span
            className="w-3 h-3 rounded-[4px] shrink-0"
            style={{ background: chip.tone, boxShadow: `0 0 14px ${chip.tone}99` }}
          />
          <span className="font-mono-ui text-xs sm:text-[13px] tracking-wide text-strong whitespace-nowrap font-medium">
            {chip.label}
          </span>
        </div>
      </div>
    </motion.div>
  );
}

/* The right-hand overview card with per-scene stats — liquid glass */
function StatsCard({ stats, reduce }) {
  return (
    <motion.div
      initial={reduce ? { opacity: 1 } : { opacity: 0, x: 32 }}
      animate={{ opacity: 1, x: 0 }}
      exit={reduce ? { opacity: 0 } : { opacity: 0, x: 24 }}
      transition={{ delay: 0.35, duration: 0.6, ease: 'easeOut' }}
      whileHover={reduce ? undefined : { scale: 1.04, y: -4 }}
      className="liquid-glass absolute right-[2%] top-[6%] w-[188px] rounded-2xl p-5 z-10"
    >
      <div className="eyebrow !text-[9px] mb-4">Overview</div>
      <div className="space-y-4">
        {stats.map(([v, l]) => (
          <div key={l}>
            <div className="font-display text-2xl font-bold text-strong leading-none">{v}</div>
            <div className="text-[11px] text-faint mt-1.5">{l}</div>
          </div>
        ))}
      </div>
    </motion.div>
  );
}

export default function HeroShowcase() {
  const reduce = useReducedMotion();
  const { t } = useLocale();
  const [index, setIndex] = useState(0);
  const [paused, setPaused] = useState(false);
  const [tilt, setTilt] = useState({ x: 0, y: 0 });
  const stageRef = useRef(null);
  const scene = SCENES[index];

  useEffect(() => {
    if (reduce || paused) return undefined;
    const t2 = setTimeout(() => setIndex((i) => (i + 1) % SCENES.length), HOLD_MS);
    return () => clearTimeout(t2);
  }, [index, paused, reduce]);

  const chipTone = useMemo(() => scene.chips[1]?.tone || '#a855f7', [scene]);

  const handlePointerMove = (e) => {
    if (reduce) return;
    const rect = e.currentTarget.getBoundingClientRect();
    const x = (e.clientX - rect.left) / rect.width - 0.5;
    const y = (e.clientY - rect.top) / rect.height - 0.5;
    setTilt({ x, y });
  };

  return (
    <section className="pt-28 pb-6 px-3 sm:px-5 lg:px-8">
      <div
        className="stage-panel relative overflow-hidden container mx-auto"
        style={{ backgroundColor: `hsl(${scene.tint})` }}
        onMouseEnter={() => setPaused(true)}
        onMouseLeave={() => { setPaused(false); setTilt({ x: 0, y: 0 }); }}
        onMouseMove={handlePointerMove}
      >
        {/* light-trails photography behind the glass */}
        <img
          src="/images/light-trails.jpg"
          alt=""
          aria-hidden="true"
          className="absolute inset-0 w-full h-full object-cover opacity-25 mix-blend-luminosity pointer-events-none"
        />
        {/* drifting liquid blobs */}
        <div className="liquid-blob absolute -top-24 -left-24 w-[420px] h-[420px] rounded-full bg-purple-500/20 blur-3xl pointer-events-none" />
        <div className="liquid-blob absolute -bottom-32 right-0 w-[480px] h-[480px] rounded-full bg-pink-500/15 blur-3xl pointer-events-none" style={{ animationDelay: '-8s' }} />
        {/* soft grain of grid over the tint */}
        <div className="absolute inset-0 bg-syncgrid opacity-60 pointer-events-none" />

        <div className="relative z-10 grid grid-cols-1 lg:grid-cols-[1.05fr_1fr] gap-10 items-center px-7 sm:px-12 lg:px-16 py-16 lg:py-24 min-h-[560px]">
          {/* ------- left: rotating copy ------- */}
          <div>
            <AnimatePresence mode="wait">
              <motion.div
                key={scene.id}
                initial={reduce ? { opacity: 1 } : { opacity: 0, y: 18 }}
                animate={{ opacity: 1, y: 0 }}
                exit={reduce ? { opacity: 0 } : { opacity: 0, y: -14 }}
                transition={{ duration: 0.5, ease: 'easeOut' }}
                className="space-y-6"
              >
                <div className="eyebrow">{scene.eyebrow}</div>
                <h1 className="font-display text-4xl sm:text-5xl lg:text-[3.05rem] font-bold text-strong leading-[1.06] max-w-xl">
                  {scene.title[0]}
                  <br />
                  <span className="ink-grad">{scene.title[1]}</span>
                </h1>
                <p className="text-lg text-soft max-w-md leading-relaxed">{scene.copy}</p>
              </motion.div>
            </AnimatePresence>

            <div className="flex flex-wrap items-center gap-4 mt-8">
              <Link to="/contact">
                <Button
                  size="lg"
                  className="bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700 text-white px-8 py-6 text-base rounded-xl transition-all duration-300 hover:scale-[1.03] shadow-xl shadow-purple-500/30 group"
                >
                  {t('cta.getDemo')}
                  <ArrowRight className="ml-2 w-5 h-5 group-hover:translate-x-1 transition-transform" />
                </Button>
              </Link>
              <Link to="/services">
                <Button
                  size="lg"
                  variant="outline"
                  className="liquid-glass border-0 text-strong px-7 py-6 text-base rounded-xl transition-all duration-300"
                >
                  {t('cta.exploreServices')}
                </Button>
              </Link>
            </div>

            {/* scene indicators */}
            <div className="flex items-center gap-2 mt-10" role="tablist" aria-label="Featured services">
              {SCENES.map((s, i) => (
                <button
                  key={s.id}
                  role="tab"
                  aria-selected={i === index}
                  aria-label={s.eyebrow}
                  onClick={() => setIndex(i)}
                  className={`h-1.5 rounded-full transition-all duration-500 ${
                    i === index ? 'w-8 bg-strong' : 'w-3 bg-strong/25 hover:bg-strong/50'
                  }`}
                  style={{ backgroundColor: i === index ? 'hsl(var(--ink))' : 'hsl(var(--ink) / 0.25)' }}
                />
              ))}
            </div>
          </div>

          {/* ------- right: interactive liquid-glass stage ------- */}
          <div ref={stageRef} className="relative h-[380px] sm:h-[440px] hidden md:block">
            <AnimatePresence mode="wait">
              <motion.div key={scene.id} className="absolute inset-0">
                <IsoStack tone={chipTone} tilt={tilt} />
                {scene.chips.map((c, i) => (
                  <Chip key={c.label} chip={c} index={i} reduce={reduce} constraintsRef={stageRef} />
                ))}
                <StatsCard stats={scene.stats} reduce={reduce} />
              </motion.div>
            </AnimatePresence>
          </div>
        </div>
      </div>
    </section>
  );
}
